'use client'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { markAsRead } from './notification-actions'
import type { NotificationData } from './types'

interface NotificationPage {
  items: NotificationData[]
  nextCursor: string | null
  hasMore: boolean
}

export function useMarkAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (notificationId: string) => markAsRead({ notificationId }),
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] })
      await queryClient.cancelQueries({ queryKey: ['unread-count'] })

      const prevNotifications = queryClient.getQueriesData<{ pages: NotificationPage[]; pageParams: unknown[] }>({ queryKey: ['notifications'] })
      const prevCount = queryClient.getQueryData<number>(['unread-count'])

      // Optimistic: mark as read in cache
      queryClient.setQueriesData<{ pages: NotificationPage[]; pageParams: unknown[] }>(
        { queryKey: ['notifications'] },
        (old) => {
          if (!old) return old
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.map((n) =>
                n.id === notificationId
                  ? { ...n, readAt: new Date().toISOString() }
                  : n
              ),
            })),
          }
        },
      )

      // Decrement unread count
      queryClient.setQueryData<number>(['unread-count'], (old) =>
        Math.max((old ?? 1) - 1, 0)
      )

      return { prevNotifications, prevCount }
    },
    onError: (_err, _id, context) => {
      // Rollback
      if (context?.prevNotifications) {
        for (const [key, data] of context.prevNotifications) {
          queryClient.setQueryData(key, data)
        }
      }
      if (context?.prevCount !== undefined) {
        queryClient.setQueryData(['unread-count'], context.prevCount)
      }
    },
  })
}
